<script setup lang="ts">
// Feuille d'aperçu NON-modale partagée (web + mobile), épinglée au-dessus de la carte : glisse depuis
// le bas et laisse la carte manipulable dessous (pointer-events). NON-modale par choix : pas de piège
// de focus ni de scroll-lock (la carte reste l'élément principal) ; seul Échap ferme au clavier. Pour
// une liste qui doit prendre l'écran, utiliser plutôt une feuille MODALE (AppBottomSheet / IonModal).
// L'hôte la place dans le slot #pinned d'AppMap (MobMap / WebMap) : sur mobile elle suit le plein écran
// et laisse la tab bar visible.
//
// Deux présentations selon les slots fournis (mêmes principes que WebMapPopup{Activities,Observations}
// → WebMapPopup) :
// - structurée (slot #content présent) : feuille à fond bleu (blue-200), en-tête = titre/sous-titre
//   (slot par défaut) + liste blanche repliable (#content). Le compteur de la ligne repliable (#toggle,
//   au-dessus d'un séparateur) est optionnel : sans lui, seul le chevron de repli est rendu (cas
//   observations, où le compteur sert de titre).
// - simple (sinon) : feuille ENTIÈREMENT BLANCHE — en-tête blanc (bouton fermer) + séparateur, puis
//   contenu blanc libre (slot par défaut), pour l'aperçu d'une tuile (sites / structures).
// Racine <Teleport> → fallthrough auto impossible : attrs de l'hôte (dont `slot="fixed"`) re-routés via `v-bind="$attrs"`.
defineOptions({ inheritAttrs: false });

const props = defineProps<{ open: boolean; fullscreen?: boolean; teleportTo?: string }>();
const emit = defineEmits<{ close: [] }>();

// En plein écran la carte téléporte sa racine vers `teleportTo` sans y entraîner la feuille du slot
// #pinned : on la téléporte vers la même cible (positionnement `fixed`, hauteurs en `dvh` faute du
// conteneur `.group/map` qui résout les `cqh`). Sans ces props, feuille en flux, inchangée.
const teleported = computed(() => !!props.fullscreen && !!props.teleportTo);

const { t } = useI18n();
const slots = useSlots();
const contentId = useId();

const panelRef = useTemplateRef<HTMLElement>("panelRef");
// Scroller exposé (parité WebMapPopup) : une liste paginée au scroll s'en sert de racine d'observation.
const scrollEl = useTemplateRef<HTMLElement>("scrollEl");
defineExpose({ scrollEl });

// Gestes verticaux + état déplié, animés en hauteur pure (sans fondu) : swipe bas réduit la hauteur
// du contenu (puis ferme une fois replié), swipe haut sur l'en-tête la révèle, le chevron bascule.
const { expanded, rendered, panelStyle, contentStyle, toggle, resetExpanded, onHandlePointerDown, onContentPointerDown, onPanelAfterLeave } = useAppSheetSwipe({
  panelEl: () => panelRef.value,
  contentEl: () => scrollEl.value,
  hasContent: () => !!slots.content,
  onClose: () => emit("close"),
});

// Chaque réouverture repart dépliée.
watch(
  () => props.open,
  (v) => {
    if (v) resetExpanded();
  }
);

// Accessibilité clavier (web) : Échap ferme la feuille. Panneau non-modal → pas de piège de focus
// ni de scroll-lock ; le geste swipe a son alternative bouton (fermer + chevron de repli).
useAppEscapeKey(() => emit("close"), { when: () => props.open });

const BACKDROP_MIN_HEIGHT_RATIO = 0.6;
const showBackdrop = ref(false);

function measureBackdrop() {
  const panel = panelRef.value;
  if (!props.open || !panel) {
    showBackdrop.value = false;
    return;
  }
  const mapContainer = panel.closest<HTMLElement>(".group\\/map");
  const availableHeight = mapContainer?.clientHeight ?? (typeof window !== "undefined" ? window.innerHeight : 0);
  showBackdrop.value = availableHeight > 0 && panel.offsetHeight > availableHeight * BACKDROP_MIN_HEIGHT_RATIO;
}

watchEffect((onCleanup) => {
  const panel = panelRef.value;
  if (!props.open || !panel) {
    showBackdrop.value = false;
    return;
  }
  const observer = new ResizeObserver(measureBackdrop);
  observer.observe(panel);
  measureBackdrop();
  onCleanup(() => observer.disconnect());
});
</script>

<template>
  <Teleport :to="teleportTo || 'body'" :disabled="!teleported">
    <div v-bind="$attrs" :class="teleported ? 'fixed z-[60]' : 'absolute z-20'" class="inset-x-0 bottom-0 pointer-events-none">
      <Transition
        enter-active-class="transition-opacity duration-300 ease-out"
        enter-from-class="opacity-0"
        leave-active-class="transition-opacity duration-300 ease-in"
        leave-to-class="opacity-0"
      >
        <div
          v-if="open && showBackdrop"
          aria-hidden="true"
          :class="teleported ? 'h-[100dvh]' : 'h-[100cqh] group-aria-modal/map:h-[100dvh]'"
          class="pointer-events-auto absolute inset-x-0 bottom-0 -z-10 bg-black/40"
          @click="emit('close')"
        />
      </Transition>
      <Transition
        appear
        enter-active-class="transition-transform duration-300 ease-out"
        enter-from-class="translate-y-full"
        leave-active-class="transition-transform duration-300 ease-in"
        leave-to-class="translate-y-full"
        @after-leave="onPanelAfterLeave"
      >
        <div
          v-if="open"
          ref="panelRef"
          :style="panelStyle"
          :class="[
            $slots.content ? 'bg-elevated' : 'bg-white',
            teleported ? 'pb-safe-or-6' : 'pb-[var(--app-map-pinned-inset,0px)] group-aria-modal/map:pb-safe-or-6',
          ]"
          class="group/sheet pointer-events-auto overflow-hidden rounded-t-2xl"
        >
          <!-- Présentation structurée : en-tête bleu + ligne repliable (chevron ± compteur) + liste. -->
          <template v-if="$slots.content">
            <div class="bg-elevated touch-none px-4 pt-4 pb-2 select-none" @pointerdown="onHandlePointerDown">
              <div class="flex items-start gap-2">
                <div class="grow"><slot /></div>
                <UButton
                  square
                  color="neutral"
                  variant="ghost"
                  icon="icon:close"
                  class="-mt-1 -mr-1 shrink-0"
                  :aria-label="t('ui.close')"
                  @click="emit('close')"
                />
              </div>
              <button
                type="button"
                class="flex w-full items-center justify-between gap-2 pt-3 pb-1 text-sm/5.5 font-semibold text-primary"
                :class="{ 'mt-4 border-t border-blue-400': $slots.toggle }"
                :aria-label="$slots.toggle ? undefined : t('ui.list.show')"
                :aria-expanded="expanded"
                :aria-controls="contentId"
                @click="toggle"
              >
                <span v-if="$slots.toggle" class="text-left"><slot name="toggle" /></span>
                <UIcon name="icon:chevronDown" class="size-4 shrink-0 transition-transform" :class="{ 'rotate-180': expanded }" />
              </button>
            </div>
            <div
              v-if="rendered"
              ref="scrollEl"
              :id="contentId"
              :style="contentStyle"
              :class="
                teleported
                  ? 'max-h-[calc(100dvh-15rem-env(safe-area-inset-top))]'
                  : 'max-h-[calc(100cqh-15rem-env(safe-area-inset-top))] group-aria-modal/map:max-h-[calc(100dvh-15rem-env(safe-area-inset-top))]'
              "
              class="overflow-y-auto overscroll-y-contain bg-white"
              @pointerdown="onContentPointerDown"
            >
              <slot name="content" />
            </div>
          </template>

          <!-- Présentation simple : aperçu d'une tuile (sites / structures). -->
          <template v-else>
            <div class="flex touch-none justify-end border-b border-lagoon-300 p-2 select-none" @pointerdown="onHandlePointerDown">
              <UButton square color="neutral" variant="ghost" icon="icon:close" :aria-label="t('ui.close')" @click="emit('close')" />
            </div>
            <div
              :class="
                teleported
                  ? 'max-h-[calc(100dvh-15rem-env(safe-area-inset-top))]'
                  : 'max-h-[calc(100cqh-15rem-env(safe-area-inset-top))] group-aria-modal/map:max-h-[calc(100dvh-15rem-env(safe-area-inset-top))]'
              "
              class="overflow-y-auto overscroll-y-contain bg-white px-4 pt-4 pb-6"
              @pointerdown="onContentPointerDown"
            >
              <slot />
            </div>
          </template>
        </div>
      </Transition>
    </div>
  </Teleport>
</template>
